import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { OTHER_DISEASE, INTEGRATED_DISEASES } from 'utils/constants/disease';
import { useTranslation } from 'utils/modifiedTranslation';
import app from 'new-ui/app';
import { DISEASE_SUB_TYPE_QUESTION_ID_BY_DISEASE } from 'modules/questionnaire/constants';
import { PENDING, SUCCESS } from 'utils/constants/queryStatuses';
import { Skeleton } from 'components/Skeleton';
import { getUserCondition, isNewlyDiagnosedUser } from 'modules/user/utils';
import { useFinanceCategories } from 'modules/financeSupport/useFinanceCategories';
import { useFinanceSupport } from 'modules/financeSupport/useFinanceSupport';
import { QUESTION_ID } from 'modules/questionnaire/questionIds';
import { useIsMounted } from 'utils/hooks/useIsMounted';
import { useLocationSearchParameters } from 'utils/hooks/useLocationSearchParameters';
import { getQuestions } from 'new-ui/functions/getQuestions';
import { arrangeCategories } from 'components/FinanceSupportCategories/categories';
import EventsWidget from './EventsWidget';
import PreferencesWidget from './PreferencesWidget';
import TreatmentOptions from './TreatmentOptions';
// eslint-disable-next-line import/no-cycle
import { TreatmentWidget } from './TreatmentWidget/TreatmentWidget';
import ExperienceWidgetV2 from './ExperienceWidgetV2';
import ExperienceWidget from './ExperienceWidget';
import ProgramsWidget from './ProgramsWidget';
import QuotesWidget from './QuotesWidget';
import { ArticlesWidget } from './ArticlesWidget/ArticlesWidget';
import { getDrugs } from './api';
import { CustomSurveyWidget } from './CustomSurveyWidget/CustomSurveyWidget';
import { FEED_GA } from './constants';
import styles from './widgets.module.css';

const {
  treatments: {
    questionId: TREATMENTS_QID,
    subQuestionIds: {
      drugs_received: DRUGS_RECEIVED_QID,
      treatment_stop_reason: TREATMENT_STOP_REASON_QID,
      cycles: CYCLES_QID,
    },
  },
} = QUESTION_ID;

export const WIDGETS = {
  APPOINTMENTS: 'appointments',
  PREFERENCES: 'preferences',
  TREATMENT_OPTIONS: 'treatment_options',
  TREATMENT: 'treatment',
  TREATMENT_SURVEY: 'treatment_survey',
  TREATMENT_EXPERIENCE: 'treatment_experience',
  SURVEY: 'survey',
  ARTICLES: 'articles',
  SUPPORT_PROGRAM: 'support_program',
  QUOTES: 'quotes',
};

const FIRST_WIDGET_SEARCH_PARAM = 'first_widget';
const PRIORITIZED_SURVEY_SEARCH_PARAM = 'surveyjs';

const sortWidgetsByFirst = (widgets, firstKey) => widgets.sort((a, b) => {
  if (a.key === firstKey) return -1;
  if (b.key === firstKey) return 1;
  return 0;
});

const attachRef = (widget, element, refs) => {
  const currentRefs = refs.current;
  if (widget in currentRefs) {
    currentRefs[widget] = element;
  }
};

export const Widgets = () => {
  const {
    [FIRST_WIDGET_SEARCH_PARAM]: firstWidget,
    [PRIORITIZED_SURVEY_SEARCH_PARAM]: prioritizedCustomSurvey,
  } = useLocationSearchParameters();
  const hasMounted = useRef(false);
  const isMounted = useIsMounted();
  const feedViewedAnalyticsSentRef = useRef(false);
  const { t } = useTranslation();
  const profile = useSelector((state) => state.profile.data);
  const matches = useSelector((state) => state.matches);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [reviewedDrugs, setReviewedDrugs] = useState({});
  const [initialTreatments, setInitialTreatments] = useState([]);
  const [scrollTarget, setScrollTarget] = useState(null);
  const [widgetState, setWidgetState] = useState({
    [WIDGETS.TREATMENT]: { ready: true },
    [WIDGETS.TREATMENT_EXPERIENCE]: { ready: false },
    [WIDGETS.TREATMENT_SURVEY]: { ready: false },
  });

  const widgetRefs = useRef({
    [WIDGETS.TREATMENT]: null,
    [WIDGETS.TREATMENT_EXPERIENCE]: null,
    [WIDGETS.TREATMENT_SURVEY]: null,
  });

  const userTreatments = profile.condition_profile.treatments;

  const condition = getUserCondition(profile);

  const { status: matchesStatus, trials, treatments } = matches;
  const treatmentsCount = treatments.length;

  const updateWidgetReady = (widgetKey, isReady) => {
    setWidgetState((prev) => ({
      ...prev,
      [widgetKey]: { ready: isReady },
    }));
  };

  const isMatchesInitializedRef = useRef(matchesStatus === SUCCESS);
  useEffect(() => {
    if (isMatchesInitializedRef.current || matchesStatus !== SUCCESS) {
      return;
    }
    isMatchesInitializedRef.current = true;
  }, [matchesStatus]);

  useEffect(() => {
    if (scrollTarget && widgetRefs.current[scrollTarget]) {
      if (widgetState[scrollTarget].ready) {
        widgetRefs.current[scrollTarget].scrollIntoView({ behavior: 'smooth' });
        setScrollTarget(null);
      }
    }
  }, [widgetState, scrollTarget]);

  const scrollToWidget = (widget) => {
    setScrollTarget(widget);
  };

  const [changesNeed, setChangesNeed] = useState({
    [WIDGETS.TREATMENT]: 0,
    [WIDGETS.TREATMENT_EXPERIENCE]: 0,
    [WIDGETS.TREATMENT_SURVEY]: 0,
  });

  const [show, setShow] = useState({
    [WIDGETS.TREATMENT]: true,
    [WIDGETS.TREATMENT_EXPERIENCE]: false,
    [WIDGETS.TREATMENT_SURVEY]: false,
  });

  const toggleVisibility = (key, value) => {
    setShow((prevShow) => ({
      ...prevShow,
      [key]: value,
    }));
  };

  const triggerUpdateForWidget = (widgetName) => {
    setChangesNeed((prev) => ({
      ...prev,
      [widgetName]: (prev[widgetName] || 0) + 1,
    }));
  };

  const translate = (arr = []) => arr.map((el) => ({
    label: t(el.label),
    value: el.value,
  }));

  const updateReviewedDrugs = (drugId, reviewDetails) => {
    setReviewedDrugs((prevState) => ({
      ...prevState,
      [drugId]: {
        ...prevState[drugId],
        ...reviewDetails,
      },
    }));
  };

  useEffect(() => {
    if (Array.isArray(userTreatments)) {
      setInitialTreatments(userTreatments);
    }
    if (initialTreatments?.length !== userTreatments?.length) {
      setSelectedDrug(null);
      triggerUpdateForWidget(WIDGETS.TREATMENT_EXPERIENCE);
      setInitialTreatments(userTreatments);
    }
    // eslint-disable-next-line
  },[userTreatments]);

  useEffect(() => {
    setIsLoading(true);

    const fetch = async () => {
      // app.questions and getQuestions(condition) can be different
      // because app.questions is requested with additionalParams
      const questions = app.questions ?? await getQuestions(condition);
      // override existing app.questions potentially fetched with additionalParams
      app.questions = questions;

      const treatmentsQuestion = questions.find(({ id }) => id === TREATMENTS_QID);

      // some diseases (mds) don't have treatments question
      if (!treatmentsQuestion) {
        setData({
          hasTreatmentsQuestion: false,
          condition,
        });
        setIsLoading(false);
        return;
      }

      const { subQuestions: treatmentsSubQuestions } = treatmentsQuestion;

      const treatmentCyclesQuestion = treatmentsSubQuestions.find((q) => q.id === CYCLES_QID);
      const treatmentStopReasonQuestion = treatmentsSubQuestions.find((q) => q.id === TREATMENT_STOP_REASON_QID);

      const diseaseSubTypeField = DISEASE_SUB_TYPE_QUESTION_ID_BY_DISEASE[condition];
      const diseaseSubTypeValue = profile.condition_profile[DISEASE_SUB_TYPE_QUESTION_ID_BY_DISEASE[condition]];
      const nextData = await getDrugs(
        condition,
        diseaseSubTypeValue ? { diseaseSubTypeField, diseaseSubTypeValue } : {},
      );

      nextData.hasTreatmentsQuestion = true;
      nextData.hasTreatment = !!profile.condition_profile.treatments?.length;
      nextData.treatments = profile.condition_profile.treatments || [];

      nextData.condition = condition;
      nextData.topSelectedDrugs = nextData.topSelectedOptionsByDiseases[condition];

      nextData.totalDrugs = nextData.options.dropDownOptions.map(({ label, value }) => ({
        title: t(label),
        value,
      }));
      if (treatmentsQuestion && INTEGRATED_DISEASES.includes(condition)) {
        nextData.totalDrugs = treatmentsQuestion.subQuestions.find(({ id }) => id === DRUGS_RECEIVED_QID)
          .options.map(({ label, value }) => ({ title: t(label), value }));
      }
      nextData.cycles = translate(treatmentCyclesQuestion?.options);
      nextData.stopReasons = translate(treatmentStopReasonQuestion?.options)
        .filter(({ value }) => value !== 'treatment_ongoing');

      if (!isMounted()) {
        return;
      }

      if (nextData.hasTreatment) {
        toggleVisibility(WIDGETS.TREATMENT_EXPERIENCE, true);
      }
      setData(nextData);
      setIsLoading(false);
    };
    fetch();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (matchesStatus === SUCCESS && !feedViewedAnalyticsSentRef.current) {
      app.sendGoogleAnalyticsEvent(FEED_GA.category, FEED_GA.events.feedViewed, {
        state: treatmentsCount ? 'has treatment' : 'no treatment',
      });
      feedViewedAnalyticsSentRef.current = true;
    }
  }, [matchesStatus, treatmentsCount]);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return; // Skip the effect on the initial render
    }
    if (!profile.condition_profile[TREATMENTS_QID]?.length) {
      toggleVisibility(WIDGETS.TREATMENT_SURVEY, false);
      toggleVisibility(WIDGETS.TREATMENT_EXPERIENCE, false);
      toggleVisibility(
        WIDGETS.TREATMENT,
        // Do no show for patients who answered treatment_for_cancer=no
        !!profile.condition_profile?.qstack?.includes(TREATMENTS_QID),
      );
    }
    // triggerUpdateForWidget(WIDGETS.experienceWidget);
    // eslint-disable-next-line
  }, [treatments]);

  const { data: rawFinanceCategories, status: financeCategoriesStatus } = useFinanceCategories();
  const financeCategories = useMemo(() => (
    rawFinanceCategories ? arrangeCategories(rawFinanceCategories, [], []) : []
  ), [rawFinanceCategories]);

  const { matchedFinanceProgramsStatus, matchedFinancePrograms } = useFinanceSupport({ user: profile });

  const treatmentSelectHandler = (widgetName) => {
    if (widgetName === WIDGETS.TREATMENT_SURVEY) {
      setSelectedDrug(null);
      toggleVisibility(WIDGETS.TREATMENT_SURVEY, true);
      toggleVisibility(WIDGETS.TREATMENT_EXPERIENCE, false);
      toggleVisibility(WIDGETS.TREATMENT, false);
      scrollToWidget(WIDGETS.TREATMENT_SURVEY);
    }
  };

  const onClickAddExperience = () => {
    toggleVisibility(WIDGETS.TREATMENT_SURVEY, true);
    toggleVisibility(WIDGETS.TREATMENT_EXPERIENCE, false);
    toggleVisibility(WIDGETS.TREATMENT, false);
    updateWidgetReady(WIDGETS.TREATMENT_SURVEY, false);
    scrollToWidget(WIDGETS.TREATMENT_SURVEY);
  };

  const onTreatmentAdd = () => {
    triggerUpdateForWidget(WIDGETS.TREATMENT_EXPERIENCE);
    toggleVisibility(WIDGETS.TREATMENT_EXPERIENCE, true);
    toggleVisibility(WIDGETS.TREATMENT, false);
    updateWidgetReady(WIDGETS.TREATMENT_EXPERIENCE, false);
    scrollToWidget(WIDGETS.TREATMENT_EXPERIENCE);
  };

  const onTreatmentCreate = () => {
    // triggerUpdateForWidget(WIDGETS.experienceWidget);
  };

  const onClickAddMyExperance = () => {
    toggleVisibility(WIDGETS.TREATMENT_SURVEY, true);
    toggleVisibility(WIDGETS.TREATMENT_EXPERIENCE, false);
    updateWidgetReady(WIDGETS.TREATMENT_SURVEY, false);
    scrollToWidget(WIDGETS.TREATMENT_SURVEY);
  };

  const onAddExperance = (currentDrug) => {
    updateReviewedDrugs(currentDrug.name, currentDrug);
    toggleVisibility(WIDGETS.TREATMENT_EXPERIENCE, true);
    toggleVisibility(WIDGETS.TREATMENT_SURVEY, false);
  };

  const onChangeSelectedDrug = (drug) => {
    setSelectedDrug(drug);
  };

  const onClickPreviousTreatment = () => {
    toggleVisibility(WIDGETS.TREATMENT, true);
    toggleVisibility(WIDGETS.TREATMENT_SURVEY, false);
    scrollToWidget(WIDGETS.TREATMENT);
  };

  const widgetBlocks = [
    {
      key: WIDGETS.APPOINTMENTS,
      element: (
        <EventsWidget />
      ),
    },
    {
      key: WIDGETS.PREFERENCES,
      element: (
        <PreferencesWidget
          treatments={treatments}
          trials={trials}
        />
      ),
      isShown: data && trials.length && condition !== OTHER_DISEASE,
    },
    {
      key: WIDGETS.TREATMENT_OPTIONS,
      element: (
        <TreatmentOptions
          treatments={treatments}
          trials={trials}
        />
      ),
      isShown: data && trials.length && condition !== OTHER_DISEASE,
    },
    {
      key: WIDGETS.TREATMENT,
      element: (
        <TreatmentWidget
          onTreatmentAdd={onTreatmentAdd}
          onTreatmentCreate={onTreatmentCreate}
          onClickAddExperience={onClickAddExperience}
          data={data}
          onTreatmentSelect={treatmentSelectHandler}
        />
      ),
      isShown: data?.hasTreatmentsQuestion && show[WIDGETS.TREATMENT] && condition !== OTHER_DISEASE,
    },
    {
      key: WIDGETS.TREATMENT_SURVEY,
      element: (
        <ExperienceWidgetV2
          onReady={(isReady) => updateWidgetReady(WIDGETS.TREATMENT_SURVEY, isReady)}
          onClickPreviousTreatment={onClickPreviousTreatment}
          selectedDrug={selectedDrug}
          onAddExperance={onAddExperance}
          onChangeSelectedDrug={onChangeSelectedDrug}
        />
      ),
      isShown: show[WIDGETS.TREATMENT_SURVEY] && condition !== OTHER_DISEASE,
    },
    {
      key: WIDGETS.TREATMENT_EXPERIENCE,
      element: (
        <ExperienceWidget
          onReady={(isReady) => updateWidgetReady(WIDGETS.TREATMENT_EXPERIENCE, isReady)}
          reviewedDrugs={reviewedDrugs}
          changesNeed={changesNeed}
          selectedDrug={selectedDrug}
          onClickAddMyExperance={onClickAddMyExperance}
          onChangeSelectedDrug={onChangeSelectedDrug}
        />
      ),
      isShown: data?.hasTreatmentsQuestion && show[WIDGETS.TREATMENT_EXPERIENCE],
    },
    {
      key: WIDGETS.SURVEY,
      element: (
        <CustomSurveyWidget prioritizedSurvey={prioritizedCustomSurvey} />
      ),
      isShown: condition !== OTHER_DISEASE,
    },
    {
      key: WIDGETS.ARTICLES,
      element: (
        <ArticlesWidget />
      ),
      isShown: isNewlyDiagnosedUser(profile),
    },
    {
      key: WIDGETS.SUPPORT_PROGRAM,
      element: (
        <ProgramsWidget
          categories={financeCategories}
          programs={matchedFinancePrograms}
        />
      ),
      isShown: data && [financeCategoriesStatus, matchedFinanceProgramsStatus].every((i) => i === SUCCESS),
    },
    {
      key: WIDGETS.QUOTES,
      element: (
        <QuotesWidget />
      ),
      isShown: data,
    },
  ];

  const isPreloaderShown = !data || isLoading || (!isMatchesInitializedRef.current && matchesStatus === PENDING)
    || financeCategoriesStatus === PENDING || matchedFinanceProgramsStatus === PENDING;

  if (isPreloaderShown) {
    return Array.from({ length: 3 }).map((_, index) => (
      <Skeleton key={index} className={styles.widgetSkeleton} width="100%" height="250px" />
    ));
  }

  return (
    sortWidgetsByFirst(
      widgetBlocks.filter(({ isShown = true }) => isShown),
      firstWidget,
    ).map(({ key, element }) => (
      <div
        className={styles.widgetWrapper}
        key={key}
        ref={(el) => attachRef(key, el, widgetRefs)}
      >
        {element}
      </div>
    ))
  );
};
